import { Controller } from '@hotwired/stimulus';
import { domToPng } from 'modern-screenshot';

export default class ChartingChatScreenshotController extends Controller {
  static targets = ['field', 'button', 'preview', 'previewContainer', 'zoom', 'zoomImage'];

  static ITEM_SELECTOR = '[id*="definition_item_"]';

  static TRANSPARENT_BG = new Set(['rgba(0, 0, 0, 0)', 'transparent']);

  static DASHBOARD_BG_VAR = '--cd-color-base-100';

  static FALLBACK_BG = '#ffffff';

  static DATA_URL_PREFIX = /^data:image\/png;base64,/;

  #pickerActive = false;

  #currentHighlight = null;

  attach() {
    if (this.#isAttached) {
      this.detach();
    } else if (this.#pickerActive) {
      this.#stopPicker();
    } else {
      this.#startPicker();
    }
  }

  detach() {
    if (this.hasFieldTarget) this.fieldTarget.value = '';
    if (this.hasPreviewTarget) this.previewTarget.removeAttribute('src');
    if (this.hasPreviewContainerTarget) this.previewContainerTarget.hidden = true;
    if (this.hasButtonTarget) this.buttonTarget.dataset.attached = 'false';
  }

  zoomImage(event) {
    const src = event.currentTarget.getAttribute('src');
    if (!src) return;

    this.zoomImageTarget.src = src;
    this.zoomTarget.showModal();
  }

  closeZoomOnBackdrop(event) {
    if (event.target === this.zoomTarget) this.zoomTarget.close();
  }

  get #isAttached() {
    return this.buttonTarget.dataset.attached === 'true';
  }

  #startPicker() {
    this.#pickerActive = true;
    this.buttonTarget.dataset.armed = 'true';
    document.body.dataset.cdPicking = 'true';

    document.addEventListener('mousemove', (event) => this.#onPickerMove(event), true);
    document.addEventListener('click', (event) => this.#onPickerClick(event), true);
    document.addEventListener('keydown', (event) => this.#onPickerKey(event), true);
  }

  #stopPicker() {
    this.#pickerActive = false;
    this.buttonTarget.dataset.armed = 'false';
    delete document.body.dataset.cdPicking;
    this.#clearHighlight();
  }

  #onPickerMove(event) {
    if (!this.#pickerActive) return;

    this.#highlight(ChartingChatScreenshotController.#findDashboardItemAt(event));
  }

  #onPickerClick(event) {
    if (!this.#pickerActive) return;

    this.#captureFromPickerClick(event);
  }

  #onPickerKey(event) {
    if (!this.#pickerActive) return;

    if (event.key === 'Escape') this.#stopPicker();
  }

  #highlight(item) {
    if (this.#currentHighlight === item) return;

    this.#clearHighlight();
    if (!item) return;

    // eslint-disable-next-line no-param-reassign -- DOM mutation
    item.dataset.cdPickerHighlight = 'true';
    this.#currentHighlight = item;
  }

  #clearHighlight() {
    if (!this.#currentHighlight) return;

    delete this.#currentHighlight.dataset.cdPickerHighlight;
    this.#currentHighlight = null;
  }

  async #captureFromPickerClick(event) {
    const item = ChartingChatScreenshotController.#findDashboardItemAt(event);
    if (!item) return;

    event.preventDefault();
    event.stopPropagation();

    this.#stopPicker();
    this.buttonTarget.disabled = true;
    try {
      const bg = ChartingChatScreenshotController.#resolvedBackground(item);
      const dataUrl = await domToPng(item, { backgroundColor: bg });
      this.#applyScreenshot(dataUrl);
    } catch (error) {
      // eslint-disable-next-line no-console -- surface capture failure for diagnostics
      console.error('Screenshot failed', error);
    } finally {
      this.buttonTarget.disabled = false;
    }
  }

  #applyScreenshot(dataUrl) {
    this.fieldTarget.value = dataUrl.replace(ChartingChatScreenshotController.DATA_URL_PREFIX, '');
    this.previewTarget.src = dataUrl;
    this.previewContainerTarget.hidden = false;
    this.buttonTarget.dataset.attached = 'true';
  }

  static #findDashboardItemAt(event) {
    return document.elementsFromPoint(event.clientX, event.clientY)
      .filter((el) => !el.closest('.charting-chat'))
      .map((el) => el.closest(ChartingChatScreenshotController.ITEM_SELECTOR))
      .find(Boolean) ?? null;
  }

  static #resolvedBackground(item) {
    return ChartingChatScreenshotController.#firstOpaqueAncestorBackground(item)
      || ChartingChatScreenshotController.#dashboardBackgroundVar(item)
      || ChartingChatScreenshotController.FALLBACK_BG;
  }

  static #firstOpaqueAncestorBackground(item) {
    for (let el = item.parentElement; el && el !== document.body; el = el.parentElement) {
      const bg = getComputedStyle(el).backgroundColor;

      if (bg && !ChartingChatScreenshotController.TRANSPARENT_BG.has(bg)) return bg;
    }

    return null;
  }

  static #dashboardBackgroundVar(item) {
    return getComputedStyle(item).getPropertyValue(ChartingChatScreenshotController.DASHBOARD_BG_VAR).trim();
  }
}
